import { setUserIdInSentryScope } from "@utils/error/sentry";
import { loadServerContext, setCookiesFromMeristemContext } from "./context";
import { getEmailAndNameFromURLParams, getURLParams } from "./urlParams";
import { USER_IS_BOT } from "./botDetector";

import getStore from "./redux/store";
import { getActiveUser } from "@noom/hive-auth";
import { ServerContextState } from "./redux/slices/serverContext";
import {
  expectUserData,
  fetchAndInitializeAuthenticatedUser,
  fetchAndInitializeExistingUser,
  initializeNewUser,
  replayPersistedUserDataUpdates,
  updateUserData,
} from "./redux/slices/userData";
import { updateVisitorStatusState } from "./redux/slices/visitorStatus";
import { updateUserContext } from "./api/tracker";

import goto from "src/pageDefinitions/goto";
import { setInAppAutoCookie } from "./authCookieParser";
import {
  getCountryCode,
  getLanguage,
  getMeristemContext,
  getSubdivision,
} from "./meristemContext";
import * as userSegments from "./userSegment";
import * as featureSegments from "./userSegment/features";
import { initConversionTracker } from "./services/ConversionTracker";
import { clearPromoCode } from "./redux/slices/promoCode";
import { loadActiveSubscription } from "./redux/slices/billingData";
import { triggerLoadPhase } from "src/vendor";
import { getConsent } from "./consent";
import { pendingScripts } from "./scriptLoader";
import { clearCheckoutOfferTimerExpirationCookie } from "./checkoutTimer";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { measureTask } from "./timing";
import { init as urlParamsPiiInit } from "./urlParamsPii";
import visitTracker from "@noom/visit-tracker";
import { appConfig } from "src/config";
import { logInfo } from "./monitoring/logging";
import { captureException } from "./error";
import { NoomError } from "./error/NoomError";
import { authenticateUser } from "src/test/quickSilver/components/deepLinksUtils";
import { fetchUpsellAdMetadata } from "src/api/upsellAdMetadata";
import {
  clearUpsellAdMetadata,
  setUpsellAdMetadata,
  UpsellAdMetadata,
} from "./redux/slices/upsellAdData";
import { getBillingAddress } from "./api/addons/addons";
import { updateCheckoutState } from "./redux/slices/checkout";
import { restoreShopSurveyAnswersAfterLogin } from "./shopLoginSurveyAnswers";
import {
  dropLegacyFeatureParamForMembership,
  trackDroppedLegacyFeatureParam,
} from "./legacyFeatureParams";

const OFFERS_SHOP_BIOMARKERS_CONTEXT_TYPE = "offers-shop-biomarkers-test-kit";
const OFFERS_SHOP_METABOLIC_FOR_MEN_CONTEXT_TYPE =
  "offers-shop-metabolic-for-men";
const OFFERS_SHOP_METABOLIC_FOR_WOMEN_CONTEXT_TYPE =
  "offers-shop-metabolic-for-women";
const OFFERS_SHOP_NAD_CONTEXT_TYPE = "offers-shop-nad";
const OFFERS_SHOP_SERMORELIN_CONTEXT_TYPE = "offers-shop-sermorelin";
const FAST_FORWARD_CONTEXT_TYPE = "fast-forward";
const OFFERS_AD_LOCATION_POST_SIGNUP = "POST_SIGNUP_CROSS_SELL";
const OFFERS_AD_ID_BIOMARKERS_MED =
  "post_signup_cross_sell_biomarkers_med_glp1";
const OFFERS_AD_ID_BIOMARKERS_WEIGHT =
  "post_signup_cross_sell_biomarkers_weight";
const OFFERS_AD_ID_METABOLIC_MEN_MED =
  "post_signup_cross_sell_metabolic_men_med";
const OFFERS_AD_ID_METABOLIC_MEN_WEIGHT =
  "post_signup_cross_sell_metabolic_men_weight";
const OFFERS_AD_ID_METABOLIC_WOMEN_MED =
  "post_signup_cross_sell_metabolic_women_med";
const OFFERS_AD_ID_METABOLIC_WOMEN_WEIGHT =
  "post_signup_cross_sell_metabolic_women_weight";
const OFFERS_AD_ID_NAD_MEN_MED = "post_signup_cross_sell_nad_men_med";
const OFFERS_AD_ID_NAD_WOMEN_MED = "post_signup_cross_sell_nad_women_med";
const OFFERS_AD_ID_SERMORELIN_MEN_MED =
  "post_signup_cross_sell_sermorelin_men_med";
const OFFERS_AD_ID_SERMORELIN_WOMEN_MED =
  "post_signup_cross_sell_sermorelin_women_med";
const BIOMARKERS_BILLING_ADDRESS_CONTEXT_TYPES = [
  FAST_FORWARD_CONTEXT_TYPE,
  OFFERS_SHOP_BIOMARKERS_CONTEXT_TYPE,
  OFFERS_SHOP_METABOLIC_FOR_MEN_CONTEXT_TYPE,
  OFFERS_SHOP_METABOLIC_FOR_WOMEN_CONTEXT_TYPE,
];
const CROSS_SELL_AD_METADATA_CONTEXT_TYPES = [
  OFFERS_SHOP_BIOMARKERS_CONTEXT_TYPE,
  OFFERS_SHOP_METABOLIC_FOR_MEN_CONTEXT_TYPE,
  OFFERS_SHOP_METABOLIC_FOR_WOMEN_CONTEXT_TYPE,
  OFFERS_SHOP_NAD_CONTEXT_TYPE,
  OFFERS_SHOP_SERMORELIN_CONTEXT_TYPE,
];

function getCrossSellAdMetadata(): UpsellAdMetadata | null {
  const contextType = getMeristemContext().context_type;

  if (!CROSS_SELL_AD_METADATA_CONTEXT_TYPES.includes(contextType)) {
    return null;
  }

  if (contextType === OFFERS_SHOP_NAD_CONTEXT_TYPE) {
    return {
      offersAdId: userSegments.isMaleGender()
        ? OFFERS_AD_ID_NAD_MEN_MED
        : OFFERS_AD_ID_NAD_WOMEN_MED,
      offersAdLocation: OFFERS_AD_LOCATION_POST_SIGNUP,
    };
  }

  if (contextType === OFFERS_SHOP_SERMORELIN_CONTEXT_TYPE) {
    return {
      offersAdId: userSegments.isMaleGender()
        ? OFFERS_AD_ID_SERMORELIN_MEN_MED
        : OFFERS_AD_ID_SERMORELIN_WOMEN_MED,
      offersAdLocation: OFFERS_AD_LOCATION_POST_SIGNUP,
    };
  }

  if (contextType === OFFERS_SHOP_METABOLIC_FOR_MEN_CONTEXT_TYPE) {
    return {
      offersAdId: featureSegments.isEligibleForMetabolicForMenCrossSellWeight()
        ? OFFERS_AD_ID_METABOLIC_MEN_WEIGHT
        : OFFERS_AD_ID_METABOLIC_MEN_MED,
      offersAdLocation: OFFERS_AD_LOCATION_POST_SIGNUP,
    };
  }

  if (contextType === OFFERS_SHOP_METABOLIC_FOR_WOMEN_CONTEXT_TYPE) {
    return {
      offersAdId:
        featureSegments.isEligibleForMetabolicForWomenCrossSellWeight()
          ? OFFERS_AD_ID_METABOLIC_WOMEN_WEIGHT
          : OFFERS_AD_ID_METABOLIC_WOMEN_MED,
      offersAdLocation: OFFERS_AD_LOCATION_POST_SIGNUP,
    };
  }

  if (contextType === OFFERS_SHOP_BIOMARKERS_CONTEXT_TYPE) {
    if (featureSegments.isEligibleForBiomarkersCrossSellMed()) {
      return {
        offersAdId: OFFERS_AD_ID_BIOMARKERS_MED,
        offersAdLocation: OFFERS_AD_LOCATION_POST_SIGNUP,
      };
    }

    if (featureSegments.isEligibleForBiomarkersCrossSellWeight()) {
      return {
        offersAdId: OFFERS_AD_ID_BIOMARKERS_WEIGHT,
        offersAdLocation: OFFERS_AD_LOCATION_POST_SIGNUP,
      };
    }
  }

  return null;
}

/**
 * Update UserData slice (and possibly fetch an existing user's UserData).
 */
export async function updateUserDataSlice(serverContext: ServerContextState) {
  const store = getStore();

  // Fetch user from backend if they are in the database.
  if (featureSegments.shouldFetchAuthenticatedUserData()) {
    await store.dispatch(fetchAndInitializeAuthenticatedUser());
    restoreShopSurveyAnswersAfterLogin();
  } else if (expectUserData()) {
    await store.dispatch(fetchAndInitializeExistingUser(serverContext.user_id));
  } else {
    await store.dispatch(initializeNewUser(serverContext));
  }
  const emailAndName = getEmailAndNameFromURLParams();
  if (Object.keys(emailAndName).length !== 0) {
    await store.dispatch(updateUserData(emailAndName));
  }

  await store.dispatch(replayPersistedUserDataUpdates());
}

/**
 * Track changes to the user's visitor status to mixpanel
 *
 * 1. Add tracking observer to the visitorStatus slice in Redux.
 * 2. Track initial state.
 */
function trackVisitorState() {
  // Update visitor status state on initialization to kick off VisitorStateDetermined tracking
  // Only update visitor status state on landing and payment to avoid unnecessary duplicate
  // visitorStateDetermined tracking events on post checkout contexts
  if (
    getMeristemContext().context_type === "landing" ||
    getMeristemContext().context_type === "payment-checkout"
  ) {
    getStore().dispatch(updateVisitorStatusState());
  }
}

function setInitialPeopleProperties() {
  const properties: JsonObject = {
    country: getCountryCode(),
    subdivision: getSubdivision(),
    language: getLanguage(),
  };
  if (USER_IS_BOT) properties.userIsBot = true;
  updateUserContext(properties);
}

function successCallback(text: string) {
  logInfo(text);
}

function errorCallback(error: Error) {
  captureException(
    new NoomError(`Visit tracking failed`, {
      cause: error,
      delegateMetric: true,
    })
  );
}

/**
 * Initialize redux, set up services, and fire off tracking calls.
 * @returns App initializes successfully
 */
export default async function initializeApplication() {
  // Expose debug tools
  (window as any).reduxState = getStore().getState;
  (window as any).grow = {
    getConsent,
    pendingScripts,
    setInAppAutoCookie,
    authenticateUser,
    goto,
    userSegments,
    featureSegments,
    clearPromoCode: () => getStore().dispatch(clearPromoCode()),
    clearCheckoutOfferTimerExpirationCookie,
    getSurveyAnswers,
  };

  // 1 - get the meristemContext data
  const meristemContext = getMeristemContext();
  setCookiesFromMeristemContext(meristemContext);

  // 1.5 - drop deprecated feature= params for membership users before anything reads the URL
  dropLegacyFeatureParamForMembership();

  // 2 - get serverContext from Django API
  const serverContext = await measureTask("serverContext", loadServerContext);

  // 5 - set up Sentry and attach user_id as a property
  setUserIdInSentryScope(serverContext.user_id);

  // 6 - Initialize visitTracker. This must happen before we fire any mixpanel events.
  visitTracker.init(
    appConfig.API_DOMAIN,
    appConfig.COOKIE_DOMAIN,
    successCallback,
    errorCallback
  );

  // 7 - update the UserData slice in redux (also, fetch userData if an existing user)
  await measureTask("userData", () => updateUserDataSlice(serverContext));

  // 7.25 - eagerly load active subscription if user is authenticated
  if (
    getActiveUser()?.accessCode &&
    (userSegments.isUpsellRoute() || featureSegments.isInAppMedUpgrade())
  ) {
    await getStore().dispatch(loadActiveSubscription());
  }

  // 7.5 - fetch upsell ad metadata if arriving from an upsell deep link.
  // Ad metadata is session-persisted, so it has to be re-resolved on every page load:
  // otherwise an ad seen earlier in the same tab stays attached to every subsequent
  // event, and gets credited for a purchase the user reached some other way.
  getStore().dispatch(clearUpsellAdMetadata());
  const upsellAdId = getURLParams()?.upsell_ad_id;
  if (upsellAdId) {
    try {
      const metadata = await fetchUpsellAdMetadata(upsellAdId);
      if (metadata) {
        getStore().dispatch(
          setUpsellAdMetadata({
            offersAdId: upsellAdId,
            offersAdLocation: metadata.location
              ? String(metadata.location)
              : undefined,
            offersPlacementName: metadata.placementName,
            offersOfferName: metadata.offerName,
          })
        );
      }
    } catch (error) {
      captureException(error, "upsell-ad-metadata-fetch-failed");
    }
  }

  // 7.5.1 - For the fast-forward and post-signup biomarkers cross-sell contexts,
  // pre-fetch the user's billing address and hydrate the checkout slice. The
  // slice is empty after the layer reroll.
  if (
    BIOMARKERS_BILLING_ADDRESS_CONTEXT_TYPES.includes(
      getMeristemContext().context_type
    )
  ) {
    try {
      const billingAddress = await getBillingAddress();
      if (billingAddress) {
        getStore().dispatch(
          updateCheckoutState({
            billingAddress: { ...billingAddress },
          })
        );
      }
    } catch (error) {
      captureException(error, "biomarkers-billing-address-fetch-failed");
    }
  }

  // 7.6 - The post-signup cross-sell flows bypass Offers SOA, so their ad
  // metadata is set manually here.
  // TODO: Remove once these flows use the real Offers SOA ad metadata system (step 7.5).
  if (!upsellAdId) {
    const crossSellAdMetadata = getCrossSellAdMetadata();
    if (crossSellAdMetadata) {
      getStore().dispatch(setUpsellAdMetadata(crossSellAdMetadata));
    }
  }

  // 8 - set up mixpanel tracker
  setInitialPeopleProperties();
  trackDroppedLegacyFeatureParam();

  // 9 - track a visit
  visitTracker.createOrUpdateVisit();

  // 10 - set up a redux observer that updates visitor state
  trackVisitorState();

  initConversionTracker();

  // Before loading pixels, hide PII in the URL, since some pixels will send an event as soon as
  // they load which can include the current URL
  urlParamsPiiInit();

  triggerLoadPhase("after-context-init");
}
